import { Fragment, ReactElement, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Yup from 'utils/validation/yup-extensions';
import { useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import { useQuery } from '../../../utils/hooks/useQuery';
import { UserContext } from 'context/User/UserContext';
import { getLegacyPath } from 'routes/_utils';
import { raiseErrorBanner } from '../../../components/MessageDispatch/messageDispatch';
import { raiseWarningBanner } from '../../../components/MessageDispatch/messageDispatch';
import { getOrganizations } from '../../RequestFlow/Agreements/components/OrganizationSearchBox/_api';
import { postAgreementInitiation } from './_api';
import { checkSimilarity } from './NewThirdPartyOrganization/_utils';
import Base from '../../Base/Base';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import FieldWrapper from 'components/Form/FieldWrapper';
import {
	Button,
	ButtonGroup,
	Checkbox,
	FAB,
} from '@upmc/ims.component-library';
import { AcceptDecline } from '../../../components/UI/Input/Custom/AcceptDecline/AcceptDecline';
import Multiselect from '../../../components/UI/Input/Multiselect/Multiselect';
import { MultiselectTypes } from '../../../components/UI/Input/Multiselect/_types';
import FormWrapper from '../../../components/Form/FormWrapper';
import { NewThirdPartyOrganization } from './NewThirdPartyOrganization/NewThirdPartyOrganization';
import { LABEL_POSITIONS } from '../../../components/Form/_constants';
import { EmailTextBox } from '../../../components/Form/TextBox/variants/EmailTextBox/EmailTextBox';
import { AlternateEmailTextBox } from '../../RequestFlow/Agreements/components/AlternateEmailTextBox/AlternateEmailTextBox';
import { DocumentSelector } from './DocumentSelector/documentSelector';
import Confirm from '../../../components/AppCart/GenericApps/Confirm/Confirm';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List, {
	ListItem,
	MDCListItemText,
	MDCListDivider,
} from '../../../components/UI/MDC/List/List';
import { Actions as CardActions } from '../../../components/UI/MDC/Card/Card';
import { HomeButton } from '../../../components/UI/Input/Button/index';
import { Tooltip } from '../../../components/UI/MDC/Tooltip/Tooltip';
import Icon from '../../../components/UI/MDC/Icon/Icon';
import {
	Headline5,
	Subtitle1,
	Subtitle2,
} from '../../../components/UI/MDC/Typography/Typography';
import Divider from '@mui/material/Divider';
import {
	ACTIONS,
	CSS_CLASSES,
	DESTINATIONS,
	FORM_INITIAL_VALUES,
	FORM_KEYS,
	FORM_VALIDATION_SCHEMA_OBJECT,
	QUERY_PARAMS,
} from './_constants';
import {
	FORM_KEYS as NEW_ORGANIZATION_FORM_KEYS,
	FORM_INITIAL_VALUES as NEW_ORGANIZATION_FORM_INITIAL_VALUES,
	FORM_VALIDATION_SCHEMA_OBJECT as NEW_ORGANIZATION_FORM_VALIDATION_SCHEMA_OBJECT,
	MINIMUM_LENGTH as NEW_ORGANIZATION_MINIMUM_LENGTH,
} from './NewThirdPartyOrganization/_constants';

import classes from './Dashboard.module.scss';
import ThirdPartyOrganization from 'pages/NewNonEmployee/components/fields/ThirdPartyOrganization';
import { NEW_NON_EMPLOYEE_KEYS } from 'pages/NewNonEmployee/_const';
import {
	checkTrackingNumber,
	getSiteDetails,
	submitLinkSiteAdminCompletion,
} from '../EpicCareLinkAdministration/_api';
/**
 * Functional component for presenting supported actions to be taken against third-party organizations.
 * @returns {ReactElement} rendered component.
 */
const Dashboard = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.allowImpersonation
		? (contextUser.userInfo.impersonationProfile ??
			contextUser.userInfo.userProfile)
		: contextUser.userInfo.userProfile;

	//#region Query parameters
	/**
	 * Type of action to be performed, if any.
	 * @type {string}
	 */
	const ACTION = useQuery().get(QUERY_PARAMS.ACTION);
	/**
	 * Type of Message Queue ID to reference, if any.
	 * @type {string}
	 */
	const MQID = useQuery().get(QUERY_PARAMS.MQ_ID);
	/**
	 * Type of tracking number to reference, if any.
	 * @type {string}
	 */
	const TN = useQuery().get(QUERY_PARAMS.TRACKING_NUMBER);
	/**
	 * Destination of procession redirect, if any.
	 * @type {string}
	 */
	const DESTINATION = useQuery().get(QUERY_PARAMS.DESTINATION);
	//#endregion

	//#region Local state
	/**
	 * @typedef {boolean} sArrLikeOrganizations Local state possessing third-party organizations with likeness to {@link ACTIONS} 'ADD' submission value.
	 */
	/**
	 *@typedef {Function} setArrLikeOrganizations Setter for third-party organizations with likeness to {@link ACTIONS} 'ADD' submission value.
	 */
	const [sArrLikeOrganizations, setArrLikeOrganizations] = useState([]);
	/**
	 * @typedef {boolean} sLoading Local state toggling page loading animation.
	 */
	/**
	 *@typedef {Function} setLoading Setter for loading animation toggle.
	 */
	const [sLoading, setLoading] = useState(true);

	/**
	 * @typedef {boolean} sActionPerformed Local state submission type.
	 */
	/**
	 *@typedef {Function} setActionPerformed Setter for submission type.
	 */
	const [sActionPerformed, setActionPerformed] = useState(null);

	/**
	 * @typedef {boolean} sConfirmSubmissionId Local state submission confirmation communication ID.
	 */
	/**
	 *@typedef {Function} setConfirmSubmissionId Setter for submission confirmation communication ID.
	 */
	const [sConfirmSubmissionId, setConfirmSubmissionId] = useState(null);

	/**
	 * @typedef {boolean} sIsOrganizationExisting Local state tracking existence of {@link ACTIONS} 'VERIFY' action third-party organization.
	 */
	/**
	 * @typedef  {Function} setIsOrganizationExisting Setter for {@link ACTIONS} 'VERIFY' action third-party organization.
	 */
	const [sIsOrganizationExisting, setIsOrganizationExisting] = useState(true);

	/**
	 * @typedef {boolean} sIsDialogOpen Local state tracking dialog visibility.
	 */
	/**
	 * @typedef  {Function} setIsDialogOpen Setter for dialog visibility.
	 */
	const [sIsDialogOpen, setIsDialogOpen] = useState(false);
	/**
	 * @typedef {boolean} sWasDialogAccepted Local state tracking whether dialog was accepted.
	 */
	/**
	 * @typedef  {Function} setWasDialogAccepted Setter for dialog acceptance.
	 */
	const [sWasDialogAccepted, setWasDialogAccepted] = useState(false);
	/**
	 * @typedef {boolean} sSiteInformation Local state tracking Link site information.
	 */
	/**
	 * @typedef  {Function} setSiteInformation Setter for Link site information.
	 */
	const [sSiteInformation, setSiteInformation] = useState(null);
	/**
	 * @typedef {boolean} sChecked Local state tracking Link site does not exist.
	 */
	/**
	 * @typedef  {Function} setChecked Setter for Link site does not exist.
	 */
	const [sChecked, setChecked] = useState(false);
	/**
	 * @typedef {boolean} sValidTrackingNumber Local state tracking if tracking number is valid
	 */
	/**
	 * @typedef  {Function} setValidTrackingNumber Setter for tracking number validity
	 */
	const [sValidTrackingNumber, setValidTrackingNumber] = useState(null);

	const dividerStyles = { margin: '1.25rem 0.75rem' };

	//#endregion

	//#region Lifecycle Hooks
	/**
	 * Lifecycle hook on local variable change. Conditionally consumes logged-on user information once available during initial loading.
	 * Pre-populates requestor email address in third-party organization creation form if available.
	 *
	 * @function
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		if (user?.ID) {
			FORM_INITIAL_VALUES[ACTIONS.ADD][FORM_KEYS.REQUESTOR] =
				user.Email ?? '';
			setLoading(false);
		}
	}, [user]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await getSiteDetails(MQID);
				if (response?.msgID > 0) {
					NEW_ORGANIZATION_FORM_INITIAL_VALUES[
						NEW_ORGANIZATION_FORM_KEYS.ORGANIZATION_NAME
					] = response.siteName;
					FORM_INITIAL_VALUES[ACTIONS.ADD][FORM_KEYS.REPRESENTATIVE] =
						response.siteAdminEmail;
					setSiteInformation(response);
				} else {
					raiseErrorBanner(
						'No Site Registration Form Data Available',
						dispatch,
					);
				}
			} catch (error) {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			} finally {
				setLoading(false);
			}
		};
		if (ACTION === ACTIONS.ADD_LINK) {
			if (MQID) {
				fetchData();
			} else {
				raiseErrorBanner(
					'No Site Registration Form Data Available',
					dispatch,
				);
			}
		}
	}, [ACTION, MQID, dispatch]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await checkTrackingNumber(TN, dispatch);
				setValidTrackingNumber(response ? true : false);
				if (!response) {
					raiseErrorBanner(
						'Invalid tracking number in URL',
						dispatch,
					);
				}
			} catch (error) {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			} finally {
				setLoading(false);
			}
		};
		if (ACTION === ACTIONS.ADD_LINK) {
			if (TN) {
				fetchData();
			} else {
				setValidTrackingNumber(false);
				raiseErrorBanner('No tracking number in URL', dispatch);
			}
		}
	}, [ACTION, TN, dispatch]);
	//#endregion

	//#region Event Handlers
	/**
	 * Asynchronously retrieves third-party organizations possessing @param val, ignoring empty and trimming non-empty strings.
	 * Imposes maximum third-party organization count for performance considerations.
	 *
	 * @function
	 * @param {string} val search criteria provided by end-user.
	 * @returns {Array[string]} third-party organizations matching search criteria.
	 */
	const onSearchHandler = async val => {
		try {
			if (val) {
				const value = String(val).trim();
				if (value.length > 0) {
					const results = await getOrganizations(value);
					const arrOrganizations =
						results.length > 200 ? [] : results;
					return arrOrganizations;
				}
			}
		} catch (error) {
			if (error.message.includes('Success')) {
				raiseWarningBanner(
					error.cause ? error.cause.message : error.message,
					dispatch,
				);
			} else {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			}
		}
	};
	/**
	 * Synchronous event handler for processing component workflow resets.
	 * @returns void
	 */
	const onResetHandler = () => {
		switch (ACTION) {
			case ACTIONS.ADD:
				NEW_ORGANIZATION_FORM_INITIAL_VALUES[
					NEW_ORGANIZATION_FORM_KEYS.ORGANIZATION_NAME
				] = '';
				FORM_INITIAL_VALUES[ACTIONS.ADD][FORM_KEYS.REPRESENTATIVE] = '';
				setConfirmSubmissionId(null);
				setWasDialogAccepted(false);
				setArrLikeOrganizations([]);
				break;
			default:
				break;
		}
	};
	/**
	 * Asynchronous event handler for processing component submissions.
	 * @param {Object} values nullable form value(s).
	 * @returns void.
	 */
	const onSubmitHandler = async (values = null, actionSpecifier = null) => {
		setLoading(true);
		const actionTaken = actionSpecifier ?? ACTION;
		try {
			switch (actionTaken) {
				case null:
				case ACTIONS.ADD:
					if (values) {
						const payload = await postAgreementInitiation(
							values[
							NEW_ORGANIZATION_FORM_KEYS.ORGANIZATION_NAME
							],
							user.ID,
							user.Name,
							values[FORM_KEYS.REQUESTOR],
							values[FORM_KEYS.REPRESENTATIVE],
							values[FORM_KEYS.DOCUMENT_SELECTOR],
							dispatch,
						);
						setConfirmSubmissionId(
							payload && payload.id ? payload.id : null,
						);
						setActionPerformed(ACTIONS.ADD);
					}
					break;
				case ACTIONS.ADD_LINK:
					if (values) {
						await submitLinkSiteAdminCompletion(values, TN).then(
							async payload => {
								setConfirmSubmissionId(payload);
								setActionPerformed(ACTIONS.ADD_LINK);
							},
						);
					}
					break;
				case ACTIONS.VERIFY:
					switch (DESTINATION) {
						case DESTINATIONS.NEW_IDENTITY:
							history.push(
								getLegacyPath('/ESO/NewIdentity.aspx?opt=0'),
							);
							break;
						default:
							history.push('/index.html');
							break;
					}
					break;
				default:
					break;
			}
		} catch (error) {
			if (error.message.includes('Success')) {
				raiseWarningBanner(
					error.cause ? error.cause.message : error.message,
					dispatch,
				);
			} else {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			}
		} finally {
			setLoading(false);
		}
	};
	//#endregion

	//#region Helper Methods
	/**
	 * Prepares @param arrButtons collection to function as MDC Card action button(s).
	 * @param  {...Node} arrButtons button element(s) to render.
	 * @returns {Node} collection of action button(s).
	 */
	const createActionButtons = (...arrButtons) => (
		<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
			<CardActions>
				<ButtonGroup>{arrButtons}</ButtonGroup>
			</CardActions>
		</StyleAuxiliary>
	);
	//#endregion

	//#region JSX
	/**
	 * Local variable possessing JSX for canceling workflow and returning home.
	 * @type {Node}
	 */
	const buttonHome = (
		<HomeButton
			id={FORM_KEYS.BUTTON_HOME}
			key={FORM_KEYS.BUTTON_HOME}
		></HomeButton>
	);
	/**
	 * Local variable possessing JSX for resetting workflow.
	 * @type {Node}
	 */
	const buttonReset = (
		<Button
			id={FORM_KEYS.BUTTON_RESET}
			key={FORM_KEYS.BUTTON_RESET}
			onClick={onResetHandler}
		>
			Submit New
		</Button>
	);
	/**
	 * Local variable possessing JSX for proceeding in workflow.
	 * @type {Node}
	 */
	const buttonSubmit = (
		<Button
			id={FORM_KEYS.BUTTON_SUBMIT}
			key={FORM_KEYS.BUTTON_SUBMIT}
			type='submit'
		>
			Submit
		</Button>
	);
	/**
	 * Local variable possessing JSX for initiating creation of new third-party organization.
	 * @description
	 * Constructs a form for submitting creation of new third-party organizations. Form submission
	 * intercepted to perform custom validation, specifically if the provided third-party organization
	 * name is similar or like existing names. If validation sucessful, form submission manually invoked;
	 * otherwise, an invasive dialog is raised to communication validation warning.
	 *
	 * @type {Node}
	 */
	const actionAdd = sConfirmSubmissionId ? (
		<Fragment>
			<Confirm
				bannerMessage='Initiation successfully submitted.'
				bodyMessage={
					<>
						Your third-party organization initiation request (
						<strong>{sConfirmSubmissionId}</strong>) has been
						successfully submitted.
					</>
				}
				className={classes[CSS_CLASSES.CARD_CONFIRM]}
			/>
			<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
				{createActionButtons(buttonHome, buttonReset)}
			</StyleAuxiliary>
		</Fragment>
	) : (
		<Fragment>
			<Headline5>Add New Organization</Headline5>
			<Subtitle2>
				Initiate third-party organization creation process.
			</Subtitle2>
			<FormWrapper
				enableReinitialize
				initialValues={Object.assign(
					FORM_INITIAL_VALUES[ACTIONS.ADD],
					NEW_ORGANIZATION_FORM_INITIAL_VALUES,
				)}
				onSubmit={async values =>
					await onSubmitHandler(values, ACTIONS.ADD)
				}
				validationSchema={Yup.object().shape({
					...NEW_ORGANIZATION_FORM_VALIDATION_SCHEMA_OBJECT,
					...FORM_VALIDATION_SCHEMA_OBJECT.ADD,
				})}
			>
				{({ formik }) => (
					<Fragment>
						<Dialog
							open={sIsDialogOpen}
							onClose={() => {
								setIsDialogOpen(false);
								setWasDialogAccepted(false);
							}}
						>
							<DialogTitle>Please Confirm</DialogTitle>
							<DialogContent>
								The following, existing third-party
								organizations were identified as similar to "
								<strong>
									{
										formik.values[
										NEW_ORGANIZATION_FORM_KEYS
											.ORGANIZATION_NAME
										]
									}
								</strong>
								":
								{
									<List
										className={
											classes[CSS_CLASSES.DIALOG_LIST]
										}
										dense
										large
										nonInteractive
									>
										{sArrLikeOrganizations.map(
											(org, index) => {
												const orgName =
													org.length >= 45
														? org.substring(0, 45) +
															'...'
														: org;
												return (
													<Fragment key={index}>
														<Tooltip
															open={
																org.length >= 45
																	? undefined
																	: false
															}
															content={org}
														>
															<ListItem>
																<MDCListItemText
																	primaryText={
																		orgName
																	}
																></MDCListItemText>
															</ListItem>
														</Tooltip>
														{index !==
															sArrLikeOrganizations.length -
															1 && (
																<MDCListDivider />
															)}
													</Fragment>
												);
											},
										)}
									</List>
								}
								Given the above list, please confirm you would
								like to proceed with creation.
							</DialogContent>
							<DialogActions>
								<Button
									variant='text'
									onClick={() => {
										setIsDialogOpen(false);
										setWasDialogAccepted(false);
									}}
								>
									Cancel
								</Button>
								<Button
									variant='text'
									onClick={() => {
										setIsDialogOpen(false);
										formik.submitForm();
										setWasDialogAccepted(true);
									}}
								>
									Proceed
								</Button>
							</DialogActions>
						</Dialog>
						<StyleAuxiliary classes={classes[CSS_CLASSES.BODY_ADD]}>
							<NewThirdPartyOrganization
								className={classes[CSS_CLASSES.FORM_INPUT]}
								formik={formik}
								inputProps={{
									minLength: NEW_ORGANIZATION_MINIMUM_LENGTH,
									required: true,
									disabled: ACTION === ACTIONS.ADD_LINK,
								}}
								labelPosition={LABEL_POSITIONS.NONE}
							/>
							<EmailTextBox
								className={classes[CSS_CLASSES.FORM_INPUT]}
								formik={formik}
								label='Your Email'
								labelPosition={LABEL_POSITIONS.NONE}
								name={FORM_KEYS.REQUESTOR}
								required
								value={
									FORM_INITIAL_VALUES[ACTIONS.ADD][
									FORM_KEYS.REQUESTOR
									]
								}
							/>
							<AlternateEmailTextBox
								className={classes[CSS_CLASSES.FORM_INPUT]}
								label={'Third-Party Representative Email'}
								labelPosition={LABEL_POSITIONS.NONE}
								name={FORM_KEYS.REPRESENTATIVE}
							/>
							<Subtitle2>
								This is distinct from the individual Staff
								Access Agreement which is automatically sent
								after the first application request for a user
								and can be acknowledged directly from the email
								the non-employee receives.
							</Subtitle2>
							<FieldWrapper
								additionalProps={{
									labelPosition: LABEL_POSITIONS.NONE,
								}}
								label=''
								meta={{
									touched:
										formik.touched[
											FORM_KEYS.CHECKBOX_ACCEPTANCE
										],
									error: formik.errors[
										FORM_KEYS.CHECKBOX_ACCEPTANCE
									],
								}}
							>
								<Checkbox
									sx={{
										marginLeft: '0px',
										marginBottom: '0px',
									}}
									name={FORM_KEYS.CHECKBOX_ACCEPTANCE}
									id={FORM_KEYS.CHECKBOX_ACCEPTANCE}
									label='I acknowledge.'
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
								/>
							</FieldWrapper>

							<DocumentSelector
								className={[
									classes[CSS_CLASSES.FORM_INPUT],
									classes[CSS_CLASSES.HIDDEN],
								].join(' ')}
								formik={formik}
								id={FORM_KEYS.DOCUMENT_SELECTOR}
							/>
						</StyleAuxiliary>
						{createActionButtons(
							buttonHome,
							<Button
								sx={{ ml: '5px' }}
								disabled={formik.isSubmitting}
								id={FORM_KEYS.BUTTON_SUBMIT}
								key={FORM_KEYS.BUTTON_SUBMIT}
								onClick={async () => {
									try {
										// perform manual pre-submission validation
										formik.setSubmitting(true);

										const arrFields = Object.keys(
											formik.values,
										);
										const objFieldsTouched =
											arrFields.reduce(
												(
													previousValue,
													currentValue,
												) => {
													previousValue[
														currentValue
													] = true;
													return previousValue;
												},
												{},
											);
										// triggers field-level validation
										formik.setTouched(objFieldsTouched);

										// trigger form-level validation
										const errors =
											await formik.validateForm();
										formik.setErrors(errors);

										if (Object.keys(errors).length < 1) {
											// perform submission-only validation if not already performed or bypassed
											if (
												!sArrLikeOrganizations.length >
												0 ||
												!sWasDialogAccepted
											) {
												let arrOrganizations =
													await checkSimilarity(
														formik.values[
															NEW_ORGANIZATION_FORM_KEYS
																.ORGANIZATION_NAME
														],
													);
												arrOrganizations = [
													...new Set([
														...arrOrganizations,
														...(await getOrganizations(
															formik.values[
																NEW_ORGANIZATION_FORM_KEYS
																	.ORGANIZATION_NAME
															],
														)),
													]),
												];
												setArrLikeOrganizations(
													arrOrganizations,
												);

												// raise dialog for user confirmation
												if (
													arrOrganizations.length > 0
												) {
													setIsDialogOpen(true);
													formik.setSubmitting(false);
													return;
												}
											}
											formik.submitForm();
										} else {
											formik.setSubmitting(false);
										}
									} catch (error) {
										if (error.message.includes('Success')) {
											raiseWarningBanner(
												error.cause
													? error.cause.message
													: error.message,
												dispatch,
											);
										} else {
											raiseErrorBanner(
												error.cause
													? error.cause
													: error,
												dispatch,
											);
										}
									}
								}}
								variant='contained'
								type='button'
							>
								Submit
							</Button>,
						)}
					</Fragment>
				)}
			</FormWrapper>
		</Fragment>
	);
	/**
	 * Local variable possessing JSX for searching within existing third-party organizations.
	 * @type {Node}
	 */
	const actionSearch = (
		<Fragment>
			<Headline5>Existing Organization Search</Headline5>
			<Subtitle2>
				Search existing collection if you are unsure the user's
				third-party organization has signed the UPMC System Access
				Agreement.
			</Subtitle2>
			<Multiselect
				actionTitle='Existing Organizations'
				options={[]}
				settings={{
					isReadOnly: true,
					isSearchable: true,
					searchHandler: onSearchHandler,
					type: MultiselectTypes.LIST,
				}}
			/>
		</Fragment>
	);

	/**
	 * Local variable possessing JSX for searching within existing third-party organizations.
	 * @type {Node}
	 */
	const actionSelect = (
		<Fragment>
			<Headline5>Associate User With Existing Organization</Headline5>
			<Subtitle2>
				Search for an existing third-party organization that represents
				your site. If you find the organization you are looking for,
				select it and click submit. If not, indicate so with the
				checkbox below.
			</Subtitle2>
			<Formik
				initialValues={{
					[NEW_NON_EMPLOYEE_KEYS.THIRD_PARTY_ORGANIZATION]: '',
				}}
				validationSchema={Yup.object().shape({
					[NEW_NON_EMPLOYEE_KEYS.THIRD_PARTY_ORGANIZATION]:
						Yup.object()
							.shape({
								organizationId: Yup.number().required(),
								organizationName: Yup.string().required(),
							})
							.default(undefined)
							.nullable()
							.required(
								'Please select the third-party organization associated with the user.',
							),
				})}
				onSubmit={async values => await onSubmitHandler(values)}
			>
				<Form>
					<ThirdPartyOrganization showNewOrgCreation={false} />
					{createActionButtons(buttonSubmit)}
				</Form>
			</Formik>
		</Fragment>
	);

	/**
	 * Local variable possessing JSX for verifying existence of third-party organization before proceeding with workflow.
	 * @type {Node}
	 */
	const actionVerify = (
		<Fragment>
			<Tooltip
				content='Create new third-party organization.'
				peek={!sIsOrganizationExisting}
				peekInterval={5000}
			>
				<FAB
					icon={<Icon icon='add' />}
					onClick={() => {
						history.push({
							pathname: 'Dashboard',
							search: `?${QUERY_PARAMS.ACTION}=` + ACTIONS.ADD,
						});
					}}
					text='Create'
				/>
			</Tooltip>
			<Headline5>Third-Party Organization Verification</Headline5>
			<Subtitle2>
				To create a new non-employee with computer access, IMS requires
				a signed UPMC System Access Agreement by the user's associated
				third-party organization.
			</Subtitle2>{' '}
			<Divider sx={{ ...dividerStyles }} />
			<StyleAuxiliary classes={classes[CSS_CLASSES.BODY_VERIFY]}>
				{actionSearch}
				<br />
				<FormWrapper
					initialValues={FORM_INITIAL_VALUES.VERIFY}
					onSubmit={async values => await onSubmitHandler(values)}
					validationSchema={Yup.object().shape(
						FORM_VALIDATION_SCHEMA_OBJECT.VERIFY,
					)}
				>
					<AcceptDecline
						acceptLabel={`The UPMC System Access Agreement for the user's intended third-party organization is on file (or) computer access will not be requested.`}
						declineLabel={`The user's intended third-party organization needs to be created and its System Access Agreement completed. I acknowledge I will not be able to submit requests otherwise.`}
						onChange={value => setIsOrganizationExisting(value)}
						name={FORM_KEYS.EXISTENCE_ACCEPTANCE}
					/>
					{!sIsOrganizationExisting && (
						<Subtitle2
							className={
								classes[CSS_CLASSES.BODY_INFORMATIONAL_LABEL]
							}
						>
							Initiate third-party organization creation with
							above icon.
						</Subtitle2>
					)}
					{createActionButtons([
						buttonHome,
						...(sIsOrganizationExisting ? [buttonSubmit] : []),
					])}
				</FormWrapper>
			</StyleAuxiliary>
		</Fragment>
	);

	const siteInformation = (
		<Fragment>
			<Headline5>EpicCare Link Site Information</Headline5>
			<Subtitle2>
				A new EpicCare Link Site request was submitted and approved by
				the assigned sponsor, but no matching third party organization
				was found in IMS.
			</Subtitle2>
			<ul>
				<li>Site Name: {sSiteInformation?.siteName}</li>
				<li>
					Site Administrator Name: {sSiteInformation?.siteAdminName}
				</li>
				<li>
					Site Administrator Email: {sSiteInformation?.siteAdminEmail}
				</li>
			</ul>
			<Subtitle2>
				Please verify the site does not exist already. If it does,
				select it to complete the request. If it does not, initiate the
				process to register this site as a new third-party organization.
			</Subtitle2>
		</Fragment>
	);

	const actionAddLink =
		sValidTrackingNumber && sSiteInformation ? (
			sConfirmSubmissionId ? (
				sActionPerformed === ACTIONS.ADD_LINK ? (
					<Fragment>
						<Confirm
							bannerMessage='Third-Party Organization Applied.'
							bodyMessage={
								<>
									The third-party organization you selected
									has been applied to the site admin and the
									request has been submitted for further
									processing. Tracking Number:
									<strong>{TN}</strong>
								</>
							}
							className={classes[CSS_CLASSES.CARD_CONFIRM]}
						/>
						<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
							{createActionButtons(buttonHome)}
						</StyleAuxiliary>
					</Fragment>
				) : (
					<Fragment>
						<Confirm
							bannerMessage='Initiation successfully submitted.'
							bodyMessage={
								<>
									Your third-party organization initiation
									request (
									<strong>{sConfirmSubmissionId}</strong>) has
									been successfully submitted.
								</>
							}
							className={classes[CSS_CLASSES.CARD_CONFIRM]}
						/>
						<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
							{createActionButtons(buttonHome)}
						</StyleAuxiliary>
					</Fragment>
				)
			) : (
				<Fragment>
					{sSiteInformation && siteInformation}
					<Divider sx={{ ...dividerStyles }} />
					<Fragment>
						{!sChecked && actionSelect}
						{sChecked && actionAdd}
						<Divider sx={{ ...dividerStyles }} />
						<Card
							sx={{
								backgroundColor: '#f5f5f5',
								border: '1px solid #ddd',
								padding: '1rem',
								marginTop: '1rem',
							}}
						>
							<Subtitle1>
								Can't find the site you are looking for? It may
								not exist. Check the box below to register a new
								organization.
							</Subtitle1>
							<Checkbox
								sx={{
									marginLeft: '0px',
									marginBottom: '0px',
								}}
								name={'DOES_NOT_EXIST_CHECKBOX'}
								id={'DOES_NOT_EXIST_CHECKBOX'}
								label='Create New Site'
								onChange={() => {
									setChecked(!sChecked);
								}}
								checked={sChecked}
							/>
						</Card>
					</Fragment>
				</Fragment>
			)
		) : (
			<Fragment>
				<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
					{createActionButtons(buttonHome)}
				</StyleAuxiliary>
			</Fragment>
		);

	//#endregion

	return (
		<Base
			helpLink={
				ACTION === ACTIONS.ADD
					? 'https://infonet.upmc.com/infonet/ls/content/1213459107542427/adding-new-organization-signing-system-agreement-pdf-1764800036'
					: ACTION === ACTIONS.VERIFY
						? 'https://infonet.upmc.com/infonet/ls/content/3485248011893007/add-non-employee-to-ims-pdf-1764799974'
						: null
			}
			loading={sLoading}
			title='Third-Party Organization Dashboard'
		>
			{ACTION === ACTIONS.ADD && actionAdd}
			{ACTION === ACTIONS.SEARCH && actionSearch}
			{ACTION === ACTIONS.VERIFY && actionVerify}
			{ACTION === ACTIONS.ADD_LINK && actionAddLink}
			{ACTION === null && (
				<Fragment>
					{actionSearch} <Divider sx={{ ...dividerStyles }} />
					{actionAdd}
				</Fragment>
			)}
		</Base>
	);
};

export { Dashboard };
